import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'

import { type ThemeName, DEFAULT_THEME } from './themes'
import { getStoredTheme, setStoredTheme, applyTheme } from './theme'

interface ThemeContextValue {
  theme: ThemeName
  setTheme: (name: ThemeName) => void
  cycleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return ctx
}

const THEMES: ThemeName[] = ['atelier', 'atelier-dark']

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (typeof window !== 'undefined') {
      return getStoredTheme()
    }
    return DEFAULT_THEME
  })

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const setTheme = useCallback((name: ThemeName) => {
    setStoredTheme(name)
    setThemeState(name)
  }, [])

  const cycleTheme = useCallback(() => {
    const idx = THEMES.indexOf(theme)
    const next = THEMES[(idx + 1) % THEMES.length]
    setTheme(next)
  }, [theme, setTheme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, cycleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
